import { App } from '@ps20-frontend/frontend-models';
import { Produkt } from '../model';
import {
  CounterContainer,
  MapWithCategoryMappingFunction,
  MapWithIndexResult,
} from './selector.interfaces';

export function mapWithCategoryIndex<T>(
  collection: T[],
  category: keyof T,
  mappingFunction: MapWithCategoryMappingFunction<T>,
): T[] {
  const initialResult: MapWithIndexResult<T> = {
    counter: {},
    elements: [],
  };
  const categoryCounts = generateCategoryCounts(collection, category);
  return collection.reduce(({ counter, elements }, element, index) => {
    const categoryValue = `${element[category]}`;
    const categoryIndex = counter[categoryValue] || 0;
    const categoryCount = categoryCounts[categoryValue];
    const newElement = mappingFunction({
      element,
      index,
      categoryIndex,
      categoryCount,
    });
    return {
      counter: { ...counter, [categoryValue]: categoryIndex + 1 },
      elements: [...elements, newElement],
    };
  }, initialResult).elements;
}

function generateCategoryCounts<T>(elements: T[], category: keyof T): CounterContainer {
  const counterContainer: CounterContainer = {};

  return elements.reduce((result, element) => {
    const categoryValue = `${element[category]}`;
    const counterValue = result[categoryValue.toString()] || 0;
    return {
      ...result,
      [categoryValue]: counterValue + 1,
    };
  }, counterContainer);
}

export function getRisikoort(appId: App, angebotsId: string, products: Array<Produkt>): string {
  return products.find(produkt => produkt.appId === appId && produkt.angebotsId === angebotsId)
    ?.versichertesRisiko;
}

export function getProdukteByRisikoort(
  appId: App,
  versichertesRisiko: string,
  products: Array<Produkt>,
): Produkt[] {
  return products.filter(
    produkt => produkt.appId === appId && produkt.versichertesRisiko === versichertesRisiko,
  );
}

export function arraysShallowEqual<T>(a: Array<T> | null, b: Array<T> | null): boolean {
  if (a === b) {
    return true;
  }
  if (a == null || b == null || a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

export function memoizeOnArrayInput<TInput, TOutput>(
  compute: (input: Array<TInput>) => Array<TOutput>,
): (input: Array<TInput>) => Array<TOutput> {
  let cachedInput: Array<TInput> | null = null;
  let cachedOutput: Array<TOutput> | null = null;
  return (input: Array<TInput>): Array<TOutput> => {
    if (cachedOutput !== null && arraysShallowEqual(cachedInput, input)) {
      return cachedOutput;
    }
    const newOutput = compute(input);
    if (cachedOutput !== null && arraysShallowEqual(cachedOutput, newOutput)) {
      cachedInput = input;
      return cachedOutput;
    }
    cachedInput = input;
    cachedOutput = newOutput;
    return newOutput;
  };
}
